import {
  addDisplayName,
  addHandlers,
  addProps,
  addState,
  addStateHandlers,
  flowMax,
} from 'ad-hok'
import { createUseStyles } from 'react-jss'
import { addApi } from 'shared/src/api'
import { isLoginError } from 'shared/src/api/types'
import { addLocation, addNavigate } from 'shared/src/utils/addNavigate'
import { trackLoginTrigger } from 'shared/src/utils/analytics'
import { addAuthContext } from 'shared/src/utils/auth'
import { typedAs } from 'shared/src/utils/typedAs'

import { addBookmarkContext } from 'components/bookmarks'
import { addTranslationHelpers } from 'utils/addTranslationHelpers'
import { ConstrainWidth } from 'utils/layout'
import { addShowRegisterContext } from 'utils/register'
import { sansSerifFontFamily, serifFontFamily } from 'utils/styles'
import backButton from '../../assets/back-button-auth.svg'
import { TextField } from '../TextField'
import { EmailVerification } from './EmailVerification'
import { RecoverPassword } from './RecoverPassword'
import { TextButton } from './TextButton'
import { OmniAuth } from './OmniAuth'

const useStyles = createUseStyles({
  container: {
    paddingLeft: 24,
    paddingRight: 24,
    paddingTop: 24,
  },
  backButton: {
    background: 'none',
    border: 'none',
    padding: 0,
  },
  title: {
    color: '#ffffff',
    fontFamily: serifFontFamily,
    fontSize: 32,
    fontWeight: 600,
    lineHeight: '140%',
    letterSpacing: 0.01,
    marginTop: 16,
    marginBottom: 12,
    fontStyle: 'italic',
  },
  subtitle: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 400,
    lineHeight: '187%',
    color: 'rgba(255, 255, 255, 0.56)',
    marginLeft: 30,
    marginBottom: 40,
    maxWidth: 300,
  },
  passwordInput: {
    marginBottom: 8,
  },
  forgotPassword: {
    fontFamily: sansSerifFontFamily,
    fontSize: 14,
    fontWeight: 500,
    lineHeight: '160%',
    letterSpacing: 0.2,
    marginBottom: 18,
    color: '#ffffff',
    opacity: 0.8,
    display: 'flex',
    justifyContent: 'space-between',
  },
  forgotPasswordText: {
    '&:hover': {
      cursor: 'pointer',
    },
  },
  wrongCredentials: {
    color: '#F57561',
  },
  button: {
    height: 64,
    width: '100%',
    marginTop: 48,
    background:
      'radial-gradient(103.73% 266.85% at 0% 16.08%, #CF8355 0%, #CEAF85 38.44%, #9B876D 100%)',
    borderRadius: 2,
    color: '#ffffff',
  },
  noAccountText: {
    fontFamily: sansSerifFontFamily,
    fontWeight: 700,
    fontSize: 14,
    color: '#ffffff',
    lineHeight: '187%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 200,
    width: '100%',
    gap: 2,
  },
  linkText: {
    fontWeight: 900,
    color: '#CBA67A',
    '&:hover': {
      cursor: 'pointer',
    },
  },
})

type LoginState =
  | 'unknown'
  | 'pendingVerification'
  | 'verified'
  | 'forgotPassword'

export const Login = flowMax(
  addDisplayName('Login'),
  addNavigate,
  addState('invalidCredentials', 'setInvalidCredentials', false),
  addStateHandlers(
    {
      email: '',
      password: '',
    },
    {
      onChangeEmail: () => (newEmail: string) => ({
        email: newEmail,
      }),
      onChangePassword: () => (newPassword: string) => ({
        password: newPassword,
      }),
    }
  ),
  addState('loginState', 'setLoginState', typedAs<LoginState>('unknown')),
  addState('isVerifyingLogin', 'setIsVerifyingLogin', false),
  addApi,
  addLocation,
  addShowRegisterContext,
  addBookmarkContext,
  addAuthContext,
  addHandlers({
    onBack:
      ({ loginState, setLoginState, navigate }) =>
      () => {
        if (loginState === 'verified') {
          setLoginState('unknown')
        } else {
          navigate(-1)
        }
      },
    onRegister:
      ({ navigate }) =>
      () => {
        navigate('/register', { replace: true })
      },
    onLogin:
      ({
        api,
        email,
        password,
        setInvalidCredentials,
        setShowRegister,
        triggerBookmarkAfterLoggingIn,
        navigate,
        location,
        setIsVerifyingLogin,
        logIn,
      }) =>
      () => {
        const getVerificationStatus = async () => {
          setIsVerifyingLogin(true)
          setInvalidCredentials(false)
          const response = await api.auth.logIn({
            email,
            password,
            resetPasswordToken: undefined,
          })

          if (isLoginError(response)) {
            if (response.error === 'incorrectEmailOrPassword') {
              setInvalidCredentials(true)
            }
          } else {
            if (location.state?.loginTrigger) {
              trackLoginTrigger(location.state.loginTrigger)
            }
            triggerBookmarkAfterLoggingIn()
            logIn()
            navigate(location.state?.returnTo ?? '/', {
              state:
                {
                  ...location.state,
                } ?? {},
              replace: true,
            })
            setShowRegister(false)
          }
          setIsVerifyingLogin(false)
        }

        getVerificationStatus()
      },
    onContinue:
      ({ api, email, setLoginState }) =>
      () => {
        const getVerificationStatus = async () => {
          const response = await api.auth.status(email, 'shopperWeb')

          if (response.status === 'pendingVerification') {
            setLoginState('pendingVerification')
          } else {
            setLoginState('verified')
          }
        }

        getVerificationStatus()
      },
    onClose:
      ({ setLoginState }) =>
      () => {
        setLoginState('unknown')
      },
    onForgotPassword:
      ({ setLoginState }) =>
      () => {
        setLoginState('forgotPassword')
      },
    resetState:
      ({ setLoginState }) =>
      () => {
        setLoginState('unknown')
      },
  }),
  addHandlers({
    onKeyPress:
      () =>
      (
        event: React.KeyboardEvent<HTMLInputElement>,
        callFunction: () => void,
        disabled: boolean
      ) => {
        if (event.key === 'Enter' && !disabled) {
          callFunction()
        }
      },
  }),
  addProps(({ email, password, isVerifyingLogin }) => ({
    onContinueDisabled: email === '',
    onLoginDisabled: email === '' || password === '' || isVerifyingLogin,
  })),
  addProps(() => ({
    classes: useStyles(),
  })),
  addTranslationHelpers,
  ({
    email,
    password,
    onChangeEmail,
    onChangePassword,
    onBack,
    onRegister,
    onLogin,
    onContinue,
    onClose,
    onForgotPassword,
    loginState,
    resetState,
    onKeyPress,
    onLoginDisabled,
    invalidCredentials,
    onContinueDisabled,
    classes,
    t,
  }) => (
    <ConstrainWidth fillHeight>
      <div className={classes.container}>
        {loginState === 'pendingVerification' ? (
          <>
            <button onClick={onClose} className={classes.backButton}>
              <img src={backButton} alt={t('close')} />
            </button>
            <EmailVerification
              email={email}
              emailType="verification"
              onClose={onClose}
            />
          </>
        ) : loginState === 'forgotPassword' ? (
          <RecoverPassword email={email} onBack={resetState} />
        ) : (
          <>
            <button onClick={onBack} className={classes.backButton}>
              <img src={backButton} alt={t('back')} />
            </button>
            <h1 className={classes.title}>
              {t(
                loginState === 'verified'
                  ? 'auth.login.enterPasswordTitle'
                  : 'auth.login.title'
              )}
            </h1>
            <p className={classes.subtitle}>
              {t(
                loginState === 'verified'
                  ? 'auth.login.enterPasswordSubtitle'
                  : 'auth.login.subtitle'
              )}
            </p>
            {loginState === 'unknown' && (
              <>
                <TextField
                  id="email"
                  label={t('auth.fields.email')}
                  value={email ?? ''}
                  onChange={onChangeEmail}
                  onKeyPress={(event) =>
                    onKeyPress(event, onContinue, onContinueDisabled)
                  }
                />
                <TextButton
                  text={t('auth.login.continueButton')}
                  onClick={onContinue}
                  disabled={onContinueDisabled}
                />
                <OmniAuth />
              </>
            )}
            {loginState === 'verified' && email && (
              <>
                <TextField
                  id="password"
                  label={t('auth.fields.password')}
                  value={password ?? ''}
                  onChange={(password) => onChangePassword(password)}
                  onKeyPress={(event) =>
                    onKeyPress(event, onLogin, onLoginDisabled)
                  }
                  type="password"
                />

                <div className={classes.forgotPassword}>
                  <span className={classes.wrongCredentials}>
                    {invalidCredentials && t('auth.login.wrongCredentials')}
                  </span>
                  <span
                    onClick={onForgotPassword}
                    className={classes.forgotPasswordText}
                  >
                    {t('auth.forgotPassword')}
                  </span>
                </div>
                <TextButton
                  text={t('auth.login.loginButton')}
                  onClick={onLogin}
                  disabled={onLoginDisabled}
                />
              </>
            )}
            <div className={classes.noAccountText}>
              {t('auth.login.noAccount')}
              <span className={classes.linkText} onClick={onRegister}>
                {t('auth.login.register')}
              </span>
            </div>
          </>
        )}
      </div>
    </ConstrainWidth>
  )
)
